import { afterNextRender, Component, computed, HostBinding, inject, input, linkedSignal, output, type Signal } from '@angular/core';
import { toDateNullable } from '@helpers';
import type { CssColor, DateRange, FontWeight } from '@appTypes';
import { CSSValuePipe } from '@pipes/css-value-pipe';
import { Translate } from '@services/core/translate';
import { Alerts } from '@services/alerts/alerts';
import { DatePicker } from '@shared/controls/date-picker/date-picker';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [DatePicker, CSSValuePipe],
  templateUrl: './date-range-picker.html',
})
export class DateRangePicker {
  // Dependencias
  public readonly t = inject(Translate).translateFn();
  private readonly alerts = inject(Alerts);

  // Inputs
  $labels = input.required<'from-to' | 'start-end' | 'start_date-end_date'>({ alias: 'labels' });
  $type = input<'date' | 'datetime-local'>('date', { alias: 'type' });
  $seconds = input<boolean>(false, { alias: 'seconds' });
  $disabled = input<boolean>(false, { alias: 'disabled' });
  $_startValue = input<string | Date | null>(null, { alias: 'startValue' });
  $startValue = linkedSignal(this.$_startValue);
  $startMin = input<Date | null, string | Date | null>(null, { alias: 'startMin', transform: v => toDateNullable(v, this.$type()) });
  $startMax = input<Date | null, string | Date | null>(null, { alias: 'startMax', transform: v => toDateNullable(v, this.$type()) });
  $startPlaceholder = input<string | null>(null, { alias: 'startPlaceholder' });
  $_endValue = input<string | Date | null>(null, { alias: 'endValue' });
  $endValue = linkedSignal(this.$_endValue);
  $endMin = input<Date | null, string | Date | null>(null, { alias: 'endMin', transform: v => toDateNullable(v, this.$type()) });
  $endMax = input<Date | null, string | Date | null>(null, { alias: 'endMax', transform: v => toDateNullable(v, this.$type()) });
  $endPlaceholder = input<string | null>(null, { alias: 'endPlaceholder' });
  $labelsFontSize = input<string>('--font-size-medium', { alias: 'labelsFontSize' });
  $inputsFontSize = input<string>('1.55rem', { alias: 'inputsFontSize' });
  $labelsWeight = input<FontWeight>('normal', { alias: 'labelsWeight' });
  $labelsColor = input<CssColor>('--black-white', { alias: 'labelsColor' });
  $gap = input<string | undefined>(undefined, { alias: 'gap' });
  $hostGap = input<string>('--gap-medium', { alias: 'hostGap' });
  $hostWidth = input<string>('fit-content', { alias: 'hostWidth' });
  $inputWidth = input<string | undefined>(undefined, { alias: 'inputWidth' });
  $vertical= input<boolean>(false, { alias: 'vertical' });
  $controlPosition = input<'default' | 'opposite' | 'top' | 'bottom'>('default', { alias: 'controlPosition' });
  $verticalAlignment = input<'start' | 'center' | 'end'>('start', { alias: 'verticalAlignment' });
  $tooltipWhenDisabled = input<string>('', { alias: 'tooltipWhenDisabled' });
  $emitAtStart = input<boolean>(false, { alias: 'emitAtStart' });

  // Outputs
  selected = output<DateRange>();

  // Signals / atributos
  startEmitionDone: boolean = false;

  // Signals computados
  $translations = computed<{ $startLabel: Signal<string>, $endLabel: Signal<string> }>(() => {
    if (this.$labels() === 'start-end')
      return { $startLabel: this.t('start'), $endLabel: this.t('end') };
    else if (this.$labels() === 'from-to')
      return { $startLabel: this.t('from__date'), $endLabel: this.t('to__date') };
    else
      return { $startLabel: this.t('start_date'), $endLabel: this.t('end_date') };
  });
  $startDateValue = computed(() => toDateNullable(this.$startValue(), this.$type()));
  $endDateValue = computed(() => toDateNullable(this.$endValue(), this.$type()));
  $endDisabled = computed(() => this.$disabled() || this.$startDateValue() === null);

  // Bindings
  @HostBinding('style.width')
  get hostWidth(): string { return this.$hostWidth(); }

  // Verificar si se recibió un valor desde el selector para establecerlo como inicial y / o si se emitirá el valor al iniciar
  constructor () {
    afterNextRender(() => {
      if (this.$emitAtStart() && !this.startEmitionDone) {
        this.selected.emit({
          start: toDateNullable(this.$_startValue(), this.$type()),
          end: toDateNullable(this.$_endValue(), this.$type()),
        });
        this.startEmitionDone = true;
      }
    });
  }

  // Maneja el evento de selección de fecha
  onValueChange(isStartDate: boolean, newValue: Date | null) {
    const startDateValue = this.$startDateValue();
    const endDateValue = this.$endDateValue();
    let mustEmit: boolean = true;

    // Validar que la fecha inicial no sea mayor a la fecha final y vice versa
    if (isStartDate) {
      if (newValue !== null && endDateValue !== null && newValue > endDateValue) {
        mustEmit = false;
        setTimeout(() => { this.updateDate(isStartDate, startDateValue, false); });
        const $message = this.t(this.$type() == 'date' ? 'cannot_select_date_after_final' : 'cannot_select_datetime_after_final');
        this.alerts.show('error', { msg: $message, autoClose: true });
      }
      else if (newValue === null)
        setTimeout(() => { this.updateDate(false, null, false); });
      else if (endDateValue === null)
        mustEmit = false;
    }
    else {
      if (newValue !== null && startDateValue !== null && newValue < startDateValue) {
        mustEmit = false;
        setTimeout(() => { this.updateDate(isStartDate, endDateValue, false); });
        const $message = this.t(this.$type() == 'date' ? 'cannot_select_date_earlier_than_initial' : 'cannot_select_datetime_earlier_than_initial');
        this.alerts.show('error', { msg: $message, autoClose: true });
      }
      else if (newValue === null)
        mustEmit = false;
    }

    this.updateDate(isStartDate, newValue, mustEmit);
  }

  // Realiza la actualización de la fecha indicada
  private updateDate(isStartDate: boolean, newValue: Date | null, mustEmit: boolean) {
    if (isStartDate)
      this.$startValue.set(newValue);
    else
      this.$endValue.set(newValue);

    if (mustEmit) {
      setTimeout(() => {
        this.selected.emit({ start: this.$startDateValue(), end: this.$endDateValue() });
      });
    }
  }
}
